import { defineStore } from 'pinia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  CastCrewDTO,
  ICastCrewDTO,
  IPaginationDTO,
  ISubmissionCastCrewDTO,
  SubmissionCastCrewDTO,
} from '@/dtos';
import { computed, Ref } from 'vue';
import {
  getAllCastAndCrewSettingsAPI,
  getCastAndCrewAPI,
  saveCastCrewAPI,
  updateCastAndCrewAPI,
  updateTalenReleaseAPI,
} from '@/services/castcrew.service';

export const useCastCrewStore = defineStore('castCrew', () => {
  const queryClient = useQueryClient();
  const getAllCastAndCrewSettings = () =>
    useQuery({
      queryKey: computed(() => ['castAndCrew-settings']),
      queryFn: async (): Promise<{ data: ICastCrewDTO[]; count: number }> => {
        const ret = await getAllCastAndCrewSettingsAPI();
        const settings = await Promise.all<ICastCrewDTO[]>(
          ret.data.map((settings) => new CastCrewDTO(settings)),
        );
        return { data: settings, count: ret.count };
      },
    });

  const getCastAndCrew = (submissionId: Ref<string>, pagination?: Ref<IPaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['castAndCrew', submissionId.value, pagination?.value]),
      queryFn: async (): Promise<{ data: ISubmissionCastCrewDTO[]; count: number }> => {
        const ret = await getCastAndCrewAPI(submissionId.value, pagination?.value);
        const objs = await Promise.all<ISubmissionCastCrewDTO[]>(
          ret.data.map((settings) => new SubmissionCastCrewDTO(settings)),
        );
        return { data: objs, count: ret.count };
      },
      enabled: computed(() => !!submissionId.value),
    });

  const updateCastAndCrew = useMutation({
    mutationFn: ({
      submissionId,
      castAndCrews,
    }: {
      submissionId: string;
      castAndCrews: ISubmissionCastCrewDTO[];
    }) => {
      return updateCastAndCrewAPI(submissionId, castAndCrews);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['castAndCrew'] });
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
    },
    onError: (err: string) => {
      console.error('Error updating cast and crew:', err);
    },
  });

  const saveCast = useMutation({
    mutationFn: (castAndCrews: ISubmissionCastCrewDTO) => {
      return saveCastCrewAPI(castAndCrews);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['castAndCrew'] });
    },
    onError: (err: string) => {
      console.error('Error updating cast and crew:', err);
    },
  });

  const updateTalentRelease = useMutation({
    mutationFn: (castAndCrews: ISubmissionCastCrewDTO) => {
      return updateTalenReleaseAPI(castAndCrews);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['castAndCrew'] });
      queryClient.invalidateQueries({ queryKey: ['releases'] });
    },
    onError: (err: string) => {
      console.error('Error updating cast and crew:', err);
    },
  });

  return {
    getAllCastAndCrewSettings,
    getCastAndCrew,
    updateCastAndCrew,
    saveCast,
    updateTalentRelease,
  };
});
